import HeadingComponent from '@/components/_atoms/Heading';
import InlineVideo, { InlineVideoProps } from '@/components/_atoms/InlineVideo';
import { Container } from '@/components/layout/Container';
import gtm from '@/lib/gtm';
import { maybeNotSet } from '@/lib/typescript-helpers';
import { extractYouTubeId } from '@/lib/youtube';
import { applyBreakpointNegativeMargins, mediaBreakpointUp } from '@/styles/layout/utils';
import { FONT_WEIGHTS } from '@/styles/typography';
import { remCalc } from '@/styles/typography/utils';
import { FC, ReactNode } from 'react';
import styled from 'styled-components';

export interface VideoSectionProps extends InlineVideoProps {
  heading?: string;
  headingOnTop: boolean;
  cta?: ReactNode;
}

const VideoSection: FC<VideoSectionProps> = (props) => {
  const Heading = () =>
    props.heading ? (
      <HeadingWrapper>
        <HeadingComponent level={1} as="h2" id="video-section-heading">
          {props.heading}
        </HeadingComponent>
      </HeadingWrapper>
    ) : null;

  return (
    <VideoContainer>
      {props.headingOnTop ? <Heading /> : null}

      <VideoWrapper>
        <InlineVideo
          videoSrc={props.videoSrc}
          videoPosterSrc={props.videoPosterSrc}
          youtubeVideoUrl={props.youtubeVideoUrl ?? undefined}
          hasSound={props.hasSound}
          onInitialPlay={() => {
            gtm.track('play_video', {
              location: 'Video Section Module',
              video_youtube_id: maybeNotSet(props.youtubeVideoUrl && extractYouTubeId(props.youtubeVideoUrl)),
              video_url: maybeNotSet(props.videoSrc),
              video_title: maybeNotSet(props.heading),
            });
          }}
        />
      </VideoWrapper>

      {!props.headingOnTop ? <Heading /> : null}

      {props.cta ? (
        <Container>
          <CTAWrapper>{props.cta}</CTAWrapper>
        </Container>
      ) : null}
    </VideoContainer>
  );
};

export default VideoSection;

// Styled Components
const VideoContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding: ${remCalc(30, 0)};
  row-gap: ${remCalc(30)};
  width: 100%;

  ${mediaBreakpointUp('desktop')} {
    row-gap: ${remCalc(60)};
  }
`;

const VideoWrapper = styled.div`
  ${applyBreakpointNegativeMargins}

  ${mediaBreakpointUp('tablet')} {
    margin: unset;
  }

  ${mediaBreakpointUp('desktop')} {
    padding: 0 ${remCalc(12)};
  }
`;

const HeadingWrapper = styled.div`
  text-align: center;
`;

const CTAWrapper = styled.div`
  text-align: center;
  margin: ${remCalc(36)} auto 0;
  display: flex;
  flex-direction: column;
  gap: 1rem;

  a {
    font-size: ${remCalc(16)};
    font-weight: ${FONT_WEIGHTS.SEMIBOLD};
  }

  ${mediaBreakpointUp('tablet')} {
    flex-direction: row;
  }
`;
